'use client'

;!function(){try{var e="undefined"!=typeof globalThis?globalThis:"undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { clsx } from 'clsx/lite'
import { useEffect, useMemo, useRef, useState } from 'react'

import { registerSpotlightParticipant } from '@/components/site/visuals/home/spotlight'
import { usePrefersReducedMotion } from '@/lib/motion/reduced-motion'

type Quote = { text: string; name: string; company: string; initials: string }

/**
 * Parses a stat value like "200+", "500.000+" (nl), "500,000+" (en), or "14 dagen" into a
 * count-up target. Values containing "%" (e.g. "100% NL") are treated as non-numeric — the
 * count-up would be misleading there, so they render as-is.
 */
function parseCountableStat(value: string): { target: number; suffix: string; separator: string | null } | null {
  const match = value.match(/^(\d[\d.,]*)(.*)$/)
  if (!match) return null
  const [, numberPart, suffix] = match
  if (suffix.includes('%')) return null
  const digits = numberPart.replace(/[.,]/g, '')
  const target = Number(digits)
  if (!Number.isFinite(target) || target <= 0) return null
  const separator = numberPart.includes('.') ? '.' : numberPart.includes(',') ? ',' : null
  return { target, suffix, separator }
}

function formatCount(n: number, separator: string | null): string {
  const rounded = Math.round(n)
  if (!separator) return String(rounded)
  return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator)
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

const COUNT_UP_DURATION_MS = 1200
const IN_VIEW_THRESHOLD = 0.3

/**
 * Renders a stat value, counting up from 0 on scroll-into-view (once) as a progressive
 * enhancement. SSR and the initial client render always show the final `value` string, so
 * no-JS, pre-hydration, and `prefers-reduced-motion: reduce` all render the stable end state.
 */
export function StatValue({ value }: { value: string }) {
  const parsed = useMemo(() => parseCountableStat(value), [value])
  const ref = useRef<HTMLDivElement>(null)
  const [display, setDisplay] = useState(value)
  // Live: switching reduced motion on mid-count cancels the count (the cleanup below) and shows the
  // final value at once. A count that already ran, or was cut short that way, is not replayed.
  const reducedMotion = usePrefersReducedMotion()
  const counted = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el || !parsed || reducedMotion || counted.current) return

    let animated = false
    let rafId: number | null = null

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry || animated || !entry.isIntersecting || entry.intersectionRatio < IN_VIEW_THRESHOLD) return
        animated = true
        counted.current = true
        observer.disconnect()

        const start = performance.now()
        const tick = (now: number) => {
          const progress = Math.min((now - start) / COUNT_UP_DURATION_MS, 1)
          if (progress >= 1) {
            setDisplay(value)
            return
          }
          const current = parsed.target * easeOutCubic(progress)
          setDisplay(`${formatCount(current, parsed.separator)}${parsed.suffix}`)
          rafId = requestAnimationFrame(tick)
        }
        rafId = requestAnimationFrame(tick)
      },
      { threshold: [0, IN_VIEW_THRESHOLD, 1] },
    )
    observer.observe(el)

    return () => {
      observer.disconnect()
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
        // A count cut short ends on its final value, not wherever it was stopped.
        setDisplay(value)
      }
    }
  }, [parsed, value, reducedMotion])

  return (
    <div ref={ref} className="font-display text-[28px]/9 text-mist-950 tabular-nums dark:text-white">
      {reducedMotion ? value : display}
    </div>
  )
}

// 6s, not 8: the timer only runs while the card holds the animation spotlight and restarts on
// every spotlight loss, so this is *uninterrupted* dwell — at 8s the second quote was realistically
// never seen. A 2–3 line quote reads in 4–6s.
const AUTO_ADVANCE_MS = 6000

/**
 * A 2-quote auto-rotating testimonial slider. Both quotes are always mounted, stacked in the
 * same grid cell, and cross-faded via CSS opacity — so SSR/first paint shows the first quote at
 * full opacity with no layout shift once JS takes over. `prefers-reduced-motion: reduce` keeps
 * the first quote static (no auto-advance); dots stay clickable either way.
 */
export function TestimonialSlider({ quotes }: { quotes: Quote[] }) {
  const [index, setIndex] = useState(0)
  const figureRef = useRef<HTMLElement | null>(null)
  const [spotlit, setSpotlit] = useState(false)

  // Register once; the registry decides when this card is the one thing on the page allowed to
  // move. Reduced motion and hidden tabs are the registry's problem, not ours.
  useEffect(() => {
    if (quotes.length < 2) return
    const node = figureRef.current
    if (!node) return
    return registerSpotlightParticipant({
      element: node,
      onActivate: () => {
        node.setAttribute('data-animating', '')
        setSpotlit(true)
      },
      onDeactivate: () => {
        node.removeAttribute('data-animating')
        setSpotlit(false)
      },
    })
  }, [quotes.length])

  // Auto-advance only while spotlit. Depending on `index` restarts the 8 s clock after a manual
  // dot click as well as after an auto-advance.
  useEffect(() => {
    if (!spotlit || quotes.length < 2) return
    const timeoutId = setTimeout(() => {
      setIndex((current) => (current + 1) % quotes.length)
    }, AUTO_ADVANCE_MS)
    return () => clearTimeout(timeoutId)
  }, [spotlit, index, quotes.length])

  if (quotes.length === 0) return null

  return (
    // `spotlit` mirrors the `data-animating` attribute, so the soft accent ring here matches the
    // spotlight ring the VisualCard-framed visuals get (see visual-card.tsx).
    <figure
      ref={figureRef}
      className={clsx(
        'flex flex-col gap-6 rounded-xl bg-mist-950/2.5 p-6 ring-[1.5px] transition-colors duration-500 ease-out ring-inset sm:p-10 dark:bg-white/5',
        spotlit ? 'ring-accent/25 dark:ring-accent-bright/30' : 'ring-transparent',
      )}
    >
      <div className="grid">
        {quotes.map((quote, quoteIndex) => (
          <div
            key={quote.name + quote.company}
            aria-hidden={quoteIndex !== index}
            className={clsx(
              'col-start-1 row-start-1 flex flex-col gap-6 transition-opacity duration-500 ease-in-out',
              quoteIndex === index ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
          >
            <blockquote className="font-display text-[23px]/8 text-pretty text-mist-950 dark:text-white">
              {quote.text}
            </blockquote>
            <figcaption className="flex items-center gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-avatar text-sm font-semibold text-white">
                {quote.initials}
              </div>
              <div className="flex flex-col">
                <span className="text-sm/6 font-semibold text-mist-950 dark:text-white">{quote.name}</span>
                <span className="text-sm/6 text-mist-600 dark:text-mist-400">{quote.company}</span>
              </div>
            </figcaption>
          </div>
        ))}
      </div>
      {quotes.length > 1 && (
        // The dot a visitor sees stays 6 px; what they hit is the 24 x 24 box around it, which is
        // the minimum WCAG 2.2 SC 2.5.8 asks of a control (Lighthouse `target-size` failed on the
        // bare 6 px buttons). That is also why the row carries no gap and pulls itself back by
        // 9 px on the top, left and bottom: flush 24 px boxes space the dots out on their own, and
        // the negative margin puts the first dot back where it sat before the boxes existed.
        <div className="-my-2.25 -ml-2.25 flex w-fit items-center">
          {quotes.map((quote, quoteIndex) => (
            <button
              key={quote.name + quote.company}
              type="button"
              onClick={() => setIndex(quoteIndex)}
              aria-label={`${quote.name} — ${quoteIndex + 1}/${quotes.length}`}
              aria-current={quoteIndex === index}
              className="flex size-6 items-center justify-center"
            >
              {/* The active dot stretches into a bar and the resting ones sit at 40% rather than
                  20%. As six identical dots at a fifth of an opacity, this read as neither a
                  control nor a position — the shape now says which one you are on, and the resting
                  dots are visible enough to say how many there are. */}
              <span
                aria-hidden="true"
                className={clsx(
                  'h-1.5 rounded-full transition-all duration-300',
                  quoteIndex === index ? 'w-4.5 bg-mist-950 dark:bg-white' : 'w-1.5 bg-mist-950/40 dark:bg-white/40',
                )}
              />
            </button>
          ))}
        </div>
      )}
    </figure>
  )
}
